import React, { useState, useCallback, memo } from 'react'
import { useDispatch } from 'react-redux'
import moment from 'moment'

import { showReduxModal } from 'Reducers/UI/actions'

import AsyncCSV from 'Components/Campaigns/Page/Conversions/AsyncCSV'
import { ButtonDeleteInline } from 'Components/Buttons/ButtonDeleteInline'
import ConversionsListTable from 'Components/Campaigns/Page/Conversions/ConversionsListTable'
import DeleteReport from 'Components/Campaigns/Page/Conversions/ModalBody/DeleteReport'

import { CONVERSION_REPORT_TYPE_SHOW } from '../../../../../shared/constants'
import { deleteConversions } from '../gql'
import { useConversionReportsRequest } from '../hook/useConversionReportsRequest'
import {
  StyledTableActionCellWrapper,
  StyledAllDeletedStatus,
  StyledUnpaidDeletedStatus,
  StyledPaidDeletedStatus,
} from '../styles'

const ConversionList = ({
  isUSFormat,
  campaignId,
  handleSuccessDeleteConversions,
  hideModal,
  startRunningDeleting,
}) => {
  const [dataStale, setDataStale] = useState(false)
  const dispatch = useDispatch()
  const {
    getConversionReports,
    getConversionsData,
  } = useConversionReportsRequest(campaignId)

  const confirmDeleteReport = async (conversionLogId, timestamp) => {
    startRunningDeleting()
    const response = await deleteConversions({
      campaignId,
      includePaid: true,
      includeUnPaid: true,
      conversionLogId: timestamp ? conversionLogId : null,
    })
    const { count } = (await response.json()).data.deleteConversions
    if (count) {
      handleSuccessDeleteConversions()
    }
  }

  const handleDeleteReport = (conversionLogId, timestamp) => {
    dispatch(
      showReduxModal({
        title: 'Delete Conversions',
        children: (
          <DeleteReport
            onConfirm={() => confirmDeleteReport(conversionLogId, timestamp)}
            onCancel={hideModal}
          />
        ),
        isReportModal: true,
        hideButtons: true,
        confirmLabel: 'Confirm',
        aspect: 'conversions',
      }),
    )
  }

  const getReportStatus = ({
    deleterNameOfPaid,
    deleterNameOfUnpaid,
    currentConversionsCount,
  }) => {
    if (
      (deleterNameOfPaid && deleterNameOfUnpaid) ||
      ((deleterNameOfPaid || deleterNameOfUnpaid) &&
        currentConversionsCount == 0)
    ) {
      return <StyledAllDeletedStatus>All Deleted</StyledAllDeletedStatus>
    }
    if (deleterNameOfPaid) {
      return <StyledPaidDeletedStatus>Paid Deleted</StyledPaidDeletedStatus>
    }
    if (deleterNameOfUnpaid) {
      return (
        <StyledUnpaidDeletedStatus>Unpaid Deleted</StyledUnpaidDeletedStatus>
      )
    }
    return 'Success'
  }

  const fetchData = useCallback(
    async args => {
      const { first, skip } = args
      const reqArgs = {
        first,
        skip,
      }
      const { conversionLogs, totalCount } = await getConversionReports(
        reqArgs,
        {
          isFresh: dataStale,
        },
      )

      const isDeletePossible = r => {
        return r.isLatest && !(r.deleterNameOfPaid || r.deleterNameOfUnpaid)
      }

      return {
        data: conversionLogs.map(r => [
          r.timestamp
            ? moment(Number(r.timestamp)).format('DD MMM YYYY, HH:mm')
            : '-',
          r.fileName || 'old conversions',
          r.fileName ? CONVERSION_REPORT_TYPE_SHOW[r.reportType] : '-',
          r.fileName
            ? getReportStatus({
                deleterNameOfPaid: r.deleterNameOfPaid,
                deleterNameOfUnpaid: r.deleterNameOfUnpaid,
                currentConversionsCount: r.currentConversionsCount,
              })
            : '-',
          r.uploaderName || '-',
          r.flyersCount,
          r.currentConversionsCount,
          `${(
            (r.paidConversionsCountFPM * 100) /
            (r.conversionsCountFPM || 1)
          ).toFixed(2)}%`,
          r.deleterNameOfPaid || '-',
          r.deleterNameOfUnpaid || '-',
          <StyledTableActionCellWrapper>
            {r.currentConversionsCount !== 0 && (
              <AsyncCSV
                isUSFormat={isUSFormat}
                conversionLogId={r.id}
                reportType={r.reportType}
                getConversionsData={getConversionsData}
              />
            )}
            {isDeletePossible(r) && (
              <ButtonDeleteInline
                onClickHandler={() => handleDeleteReport(r.id, r.timestamp)}
              />
            )}
          </StyledTableActionCellWrapper>,
        ]),
        totalCount,
      }
    },
    [isUSFormat],
  )

  return (
    <ConversionsListTable
      header={header}
      pagination={5}
      fetchData={fetchData}
      refetch={() => {
        if (dataStale) {
          setTimeout(() => setDataStale(false), 100)
          return true
        }
        return false
      }}
    />
  )
}

const header = [
  'Upload date',
  'File name',
  'Report type',
  'Status',
  'Uploaded by',
  'Codes',
  'New Conversions',
  'Paid Conversions',
  'Paid Conv. Deleted By',
  'Unpaid Conv. Deleted By',
  '',
]

export default memo(ConversionList)
